import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { deviceEventLogsService } from '../services/api';

const DeviceEventLogs = () => {
  const [searchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 50, totalCount: 0, totalPages: 0 });
  const [filters, setFilters] = useState({ 
    eventType: searchParams.get('eventType') || '',
    eventSource: '',
    thingName: searchParams.get('thingName') || '',
    search: '',
    startDate: '',
    endDate: '',
  });
  const [error, setError] = useState(null);

  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const [logsResponse, statsResponse] = await Promise.all([
        deviceEventLogsService.getAll({
          page: pagination.page,
          pageSize: pagination.pageSize,
          eventType: filters.eventType,
          eventSource: filters.eventSource,
          thingName: filters.thingName,
          search: filters.search,
          startDate: filters.startDate,
          endDate: filters.endDate,
        }),
        deviceEventLogsService.getStats({
          startDate: filters.startDate,
          endDate: filters.endDate,
        }),
      ]);
      
      setLogs(logsResponse.data || []);
      setStats(statsResponse.data || null);
      if (logsResponse.pagination) {
        setPagination(prev => ({ ...prev, ...logsResponse.pagination }));
      }
    } catch (err) {
      console.error('Error fetching Device Event Logs:', err);
      setError('Failed to load event logs');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (field, value) => {
    setFilters({ ...filters, [field]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
  };

  const getEventTypeBadge = (eventType) => {
    const typeMap = {
      device_registered: { color: 'green', icon: '✓', label: 'Registered' },
      device_pre_registered: { color: 'blue', icon: '📝', label: 'Pre-Registered' },
      registration_failed: { color: 'red', icon: '✗', label: 'Reg Failed' },
      bootstrap_auth_success: { color: 'green', icon: '🔐', label: 'Auth Success' },
      bootstrap_auth_failed: { color: 'red', icon: '🔐', label: 'Auth Failed' },
      license_activated: { color: 'green', icon: '🔑', label: 'License Activated' },
      license_activation_failed: { color: 'red', icon: '🔑', label: 'License Failed' },
      license_deactivated: { color: 'yellow', icon: '🔑', label: 'License Deactivated' },
      device_connected: { color: 'green', icon: '🟢', label: 'Connected' },
      device_disconnected: { color: 'red', icon: '🔴', label: 'Disconnected' },
      connection_status_changed: { color: 'blue', icon: '🔄', label: 'Status Changed' },
      certificate_created: { color: 'green', icon: '📜', label: 'Cert Created' },
      certificate_rotated: { color: 'blue', icon: '🔄', label: 'Cert Rotated' },
      certificate_revoked: { color: 'red', icon: '📜', label: 'Cert Revoked' },
      device_deactivated: { color: 'red', icon: '⏸️', label: 'Deactivated' },
      device_reactivated: { color: 'green', icon: '▶️', label: 'Reactivated' },
      admin_review_completed: { color: 'blue', icon: '✅', label: 'Admin Review' },
    };
    const config = typeMap[eventType] || { color: 'gray', icon: '?', label: eventType };
    return (
      <span className={`badge badge-${config.color}`} title={eventType}>
        {config.icon} {config.label}
      </span>
    );
  };

  const getEventSourceBadge = (source) => {
    const sourceMap = {
      api: { color: 'blue', label: 'API' },
      iot_lifecycle: { color: 'green', label: 'IoT Lifecycle' },
      admin_ui: { color: 'purple', label: 'Admin UI' },
      lambda: { color: 'orange', label: 'Lambda' },
    };
    const config = sourceMap[source] || { color: 'gray', label: source };
    return <span className={`badge badge-${config.color}`}>{config.label}</span>;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatEventDetails = (details) => {
    if (!details) return '-';
    const keys = Object.keys(details).slice(0, 3);
    return keys.map(k => `${k}: ${JSON.stringify(details[k]).substring(0, 15)}`).join(', ');
  };

  const eventTypes = [
    'device_registered', 'device_pre_registered', 'registration_failed',
    'bootstrap_auth_success', 'bootstrap_auth_failed',
    'license_activated', 'license_activation_failed', 'license_deactivated',
    'device_connected', 'device_disconnected', 'connection_status_changed',
    'certificate_created', 'certificate_rotated', 'certificate_revoked',
    'device_deactivated', 'device_reactivated', 'admin_review_completed',
  ];

  return (
    <div className="page">
      <div className="page-header">
        <h1>📊 Device Event Logs</h1>
        <p className="page-description">
          Complete audit trail of device lifecycle events
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Stats Cards */}
      {stats && (
        <div className="stats-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div className="stat-card" style={{ background: '#f8f9fa', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#333' }}>{stats.total}</div>
            <div style={{ fontSize: '0.75rem', color: '#666' }}>Total Events</div>
          </div>
          <div className="stat-card" style={{ background: '#d4edda', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#155724' }}>{stats.registrations}</div>
            <div style={{ fontSize: '0.75rem', color: '#155724' }}>Registrations</div>
          </div>
          <div className="stat-card" style={{ background: '#cce5ff', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#004085' }}>{stats.licenseActivations}</div>
            <div style={{ fontSize: '0.75rem', color: '#004085' }}>License Activations</div>
          </div>
          <div className="stat-card" style={{ background: '#f8d7da', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#721c24' }}>{stats.deactivations}</div>
            <div style={{ fontSize: '0.75rem', color: '#721c24' }}>Deactivations</div>
          </div>
          <div className="stat-card" style={{ background: '#fff3cd', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#856404' }}>{stats.registrationFailures}</div>
            <div style={{ fontSize: '0.75rem', color: '#856404' }}>Reg Failures</div>
          </div>
          <div className="stat-card" style={{ background: '#e2e3e5', padding: '1rem', borderRadius: '8px', textAlign: 'center' }}>
            <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#383d41' }}>{stats.uniqueDevices}</div>
            <div style={{ fontSize: '0.75rem', color: '#383d41' }}>Unique Devices</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-bar" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
        <input
          type="text"
          placeholder="🔍 Search thing name, details..."
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
          className="search-input"
          style={{ minWidth: '200px' }}
        />
        
        <select
          value={filters.eventType}
          onChange={(e) => handleFilterChange('eventType', e.target.value)}
          className="filter-select"
        >
          <option value="">All Event Types</option>
          {eventTypes.map(type => (
            <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
          ))}
        </select>

        <select
          value={filters.eventSource}
          onChange={(e) => handleFilterChange('eventSource', e.target.value)}
          className="filter-select"
        >
          <option value="">All Sources</option>
          <option value="api">API</option>
          <option value="iot_lifecycle">IoT Lifecycle</option>
          <option value="admin_ui">Admin UI</option>
          <option value="lambda">Lambda</option>
        </select>

        <input
          type="text"
          placeholder="Thing Name..."
          value={filters.thingName}
          onChange={(e) => handleFilterChange('thingName', e.target.value)}
          className="filter-select"
          style={{ width: '150px' }}
        />

        <input
          type="date"
          value={filters.startDate}
          onChange={(e) => handleFilterChange('startDate', e.target.value)}
          className="filter-select"
        />

        <input
          type="date"
          value={filters.endDate}
          onChange={(e) => handleFilterChange('endDate', e.target.value)}
          className="filter-select"
        />

        <button 
          onClick={() => setFilters({ eventType: '', eventSource: '', thingName: '', search: '', startDate: '', endDate: '' })}
          className="btn btn-secondary btn-sm"
        >
          Clear Filters
        </button>
      </div>

      {/* Logs Table */}
      {loading ? (
        <div className="loading">Loading event logs...</div>
      ) : logs.length === 0 ? (
        <div className="empty-state">
          <p>No event logs found</p>
          {filters.eventType && <p>Try adjusting your filters</p>}
        </div>
      ) : (
        <>
          <div className="table-container" style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Event Type</th>
                  <th>Source</th>
                  <th>Device</th>
                  <th>Details</th>
                  <th>IP / Admin</th>
                </tr>
              </thead>
              <tbody>
                {logs.map(log => (
                  <tr key={log.id}>
                    <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(log.createdAt)}</td>
                    <td>{getEventTypeBadge(log.eventType)}</td>
                    <td>{getEventSourceBadge(log.eventSource)}</td>
                    <td>
                      {log.thingName ? (
                        <Link to={`/ggos-devices/${log.thingName}`}>
                          🖥️ {log.thingName}
                        </Link>
                      ) : (
                        <span className="text-muted">N/A</span>
                      )}
                    </td>
                    <td style={{ fontFamily: 'monospace', fontSize: '0.75rem', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {formatEventDetails(log.eventDetails)}
                    </td>
                    <td>
                      {log.adminEmail ? (
                        <span title={log.adminEmail}>👤 {log.adminEmail.split('@')[0]}</span>
                      ) : log.ipAddress ? (
                        <span>{log.ipAddress}</span>
                      ) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="pagination-btn"
            >
              ← Previous
            </button>
            <span className="pagination-info">
              Page {pagination.page} of {pagination.totalPages} 
              ({pagination.totalCount} total events)
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
              className="pagination-btn"
            >
              Next →
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default DeviceEventLogs;
